import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { CATALOGS } from '../../resources/lang/registry.generated';
import LocalizationManager from './LocalizationManager';
import moment from 'moment';
import { getStorefrontLocale } from '../../modules/storefront/exports/Cookies';
import { I18N_RESOLUTION_OPTIONS } from './i18nOptions';
import { baseLanguage } from './Languages';

const DEFAULT_LANGUAGE = 'en';

const languageDetector = {
  type: 'languageDetector',
  // If this is set to true, your detect function receives a callback function that you should call with your language,
  //useful to retrieve your language stored in AsyncStorage for example
  async: true,
  init: () => {},
  detect: (callback) => {
    // Every source below is verbatim external input: the storefront's hand-off cookie,
    // a `?lng=`, localStorage, the browser. Any of them can carry a region-flavoured
    // tag, and i18next collapses only the hyphen form — `pl_PL` would reach resource
    // lookup intact and render English. Normalise once, here, rather than at each
    // return. See baseLanguage() and tests/services/localization/i18nOptions.test.ts.
    const respond = (language) => callback(baseLanguage(language) ?? DEFAULT_LANGUAGE);

    // The first three sources are the customer's own: the storefront hand-off, an
    // explicit ?lng=, and a choice cached from an earlier visit. Flag them here, before
    // i18next caches its result, so the restaurant's configured language knows whether
    // it may apply as a default (see InitialLoadingManager.applyCompanyLanguage).
    if (getStorefrontLocale()) {
      LocalizationManager.markExplicitChoice();

      return respond(getStorefrontLocale());
    }

    let languageFromUrl = LocalizationManager.getLanguageFromUrl();

    if (languageFromUrl) {
      LocalizationManager.markExplicitChoice();

      return respond(languageFromUrl);
    }

    const browserLanguage = LocalizationManager.getLanguage();

    if (browserLanguage) {
      LocalizationManager.markExplicitChoice();
    }

    if (!browserLanguage) {
      const preferredLanguage = LocalizationManager.getPreferredLanguage();

      if (!preferredLanguage) {
        return respond(DEFAULT_LANGUAGE);
      }

      return respond(preferredLanguage);
    }

    respond(browserLanguage);
  },
  cacheUserLanguage: (lng) => {
    //Cache language if necessary
    // Normalised too: moment keys its locales the same way, and this value is what a
    // later visit reads back out of localStorage.
    const language = baseLanguage(lng) ?? DEFAULT_LANGUAGE;

    LocalizationManager.saveLanguage(language);
    moment.locale(language);
  },
};

i18n
  .use(languageDetector)
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    resources: CATALOGS,
    react: {
      useSuspense: false,
    },
    // fallbackLng, supportedLngs, load and nonExplicitSupportedLngs together — see
    // i18nOptions.ts. Spread rather than restated so the unit test that spins up a
    // real i18next instance is asserting on the options this file actually uses.
    ...I18N_RESOLUTION_OPTIONS,
    interpolation: {
      escapeValue: false,
    },
  });
